import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MDButton from "../../../../components/MDButton";
import Icon from "@mui/material/Icon";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { get, post } from "requests";
import React, { useEffect, useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMerchantContext } from "./MerchantContext";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";

const DBAInformation = () => {
  const {
    dbaInformation,
    setDbaInformationData,
    merchantBusinessInfo,
    merchantAndOwners,
    fetchMerchantData,
  } = useSubmissionStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dbaName, setDbaName] = useState("");
  const [dbas, setDBAs] = useState<string[]>(() => dbaInformation || []);
  const { clearData } = useMerchantContext();
  const [merchantDetails, setMerchantDetails] = useState(null);

  useEffect(() => {
    if (clearData) {
      setDBAs([]);
      setDbaName("");
      setMerchantDetails(null);
    }
  }, [clearData]);

  useEffect(() => {
    if (merchantBusinessInfo?.merchantId) {
      if (merchantAndOwners?.merchantId === merchantBusinessInfo?.merchantId) {
        if (merchantAndOwners) {
          setMerchantDetails(merchantAndOwners);
          setDBAs(merchantAndOwners.dba || []);
          setDbaInformationData(merchantAndOwners.dba || []);
        }
      } else {
        fetchMerchantData(merchantBusinessInfo.merchantId);
      }
    }
  }, [merchantAndOwners, merchantBusinessInfo]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDbaName(e.target.value);
  };

  const handleAddDba = () => {
    if (dbaName.trim() !== "" && merchantDetails) {
      const updatedDbaList = [...dbas, dbaName];
      setDBAs(updatedDbaList);
      setDbaInformationData(updatedDbaList);

      const updatedMerchantDetails = {
        ...merchantDetails,
        dba: updatedDbaList,
        tenantId: Number(localStorage.getItem("tenantId")),
      };

      post("merchant/updateOrCreateMerchantAndUsers", updatedMerchantDetails)
        .then((response) => {
          if (response?.result) {
            fetchMerchantData(merchantBusinessInfo.merchantId);
          }
        })
        .catch((error) => {
          console.error("Error adding DBA:", error);
        });

      setDbaName("");
      handleDialogClose();
    }
  };

  const handleDeleteDba = (index: number) => {
    const updatedDbaList = dbas.filter((_, i) => i !== index);
    setDBAs(updatedDbaList);
    setDbaInformationData(updatedDbaList);

    if (merchantDetails) {
      const updatedMerchantDetails = {
        ...merchantDetails,
        dba: updatedDbaList,
        tenantId: Number(localStorage.getItem("tenantId")),
      };

      post("merchant/updateOrCreateMerchantAndUsers", updatedMerchantDetails)
        .then((response) => {
          if (response?.result) {
            fetchMerchantData(merchantBusinessInfo.merchantId);
          }
        })
        .catch((error) => {
          console.error("Error removing DBA:", error);
        });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        marginBottom: "12px",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "16px" }}>
          DBAs
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
          {isExpanded && (
            <MDButton variant="gradient" color="primary" onClick={handleDialogOpen}>
              + Add DBA
            </MDButton>
          )}
          <IconButton onClick={toggleExpand} sx={{ marginLeft: "8px" }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ paddingLeft: 2, paddingRight: 10 }}>
        {isExpanded &&
          dbas.map((dba, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderTop: "1px solid #ddd",
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                  boxShadow: "none",
                },
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: "500", marginLeft: "16px" }}>
                {dba}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginRight: "15px",
                  }}
                  onClick={() => handleDeleteDba(index)}
                >
                  <Icon sx={{ color: "error.main", fontSize: "13px" }}>delete</Icon>
                  <Typography
                    sx={{
                      color: "error.main",
                      fontSize: "13px",
                      fontWeight: "500",
                      marginLeft: "5px",
                    }}
                  >
                    DELETE
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
      </Box>

      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add DBA Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="DBA Name"
            type="text"
            fullWidth
            variant="outlined"
            value={dbaName}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddDba} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DBAInformation;
